import { useState, useCallback, useEffect } from 'react';

interface UseExpandStateOptions {
  /** Current visible position IDs (after filtering) */
  visibleIds: string[];
}

interface UseExpandStateReturn {
  /** Set of currently expanded position IDs */
  expandedIds: Set<string>;
  /** Toggle a single position's expanded state */
  toggle: (positionId: string) => void;
  /** Check if a position is expanded */
  isExpanded: (positionId: string) => boolean;
}

export function useExpandState({ visibleIds }: UseExpandStateOptions): UseExpandStateReturn {
  const [expandedIds, setExpandedIds] = useState<Set<string>>(new Set());

  const toggle = useCallback((positionId: string) => {
    setExpandedIds((prev) => {
      const next = new Set(prev);
      if (next.has(positionId)) {
        next.delete(positionId);
      } else {
        next.add(positionId);
      }
      return next;
    });
  }, []);

  const isExpanded = useCallback(
    (positionId: string) => expandedIds.has(positionId),
    [expandedIds]
  );

  // Prune expanded IDs that are no longer in the visible set
  useEffect(() => {
    setExpandedIds((prev) => {
      const visibleSet = new Set(visibleIds);
      const pruned = new Set<string>();
      for (const id of prev) {
        if (visibleSet.has(id)) {
          pruned.add(id);
        }
      }
      if (pruned.size === prev.size) {
        return prev;
      }
      return pruned;
    });
  }, [visibleIds]);

  return { expandedIds, toggle, isExpanded };
}
